import { min, max } from 'd3-array';
import { deepClone } from '../util/deepClone';

import Indicator from '../Indicator';
import BarMarker from '../markers/BarMarker';

/** Awesome Oscillator indicator */
export default class AwesomeOscillatorIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator Name
	 * @property {string} description - Indicator Description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'awesomeoscillator',
			name: 'Awesome Oscillator',
			description:
				"Awesome Oscillator measures short-term momentum in a security. It is displayed as a histogram of the difference between two simple moving averages of the midpoints of each period's prices.",
			inputs: [
				{ name: 'period1', value: 5 },
				{ name: 'period2', value: 34 }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.awesomeoscillator.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.awesomeoscillator[dataCopy.awesomeoscillator.length - 1].dateIndex > panelRange[1]) {
			dataCopy.awesomeoscillator.pop();
		}

		return this._min(
			min(dataCopy.awesomeoscillator || [], i => i.awesomeoscillator),
			0
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.awesomeoscillator.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.awesomeoscillator[dataCopy.awesomeoscillator.length - 1].dateIndex > panelRange[1]) {
			dataCopy.awesomeoscillator.pop();
		}

		return this._max(
			max(dataCopy.awesomeoscillator || [], i => i.awesomeoscillator),
			0
		);
	}

	/**
	 * @returns {object}
	 * @property {BarMarker} barUp
	 * @property {BarMarker} barDown
	 */
	_getMarkers() {
		return (
			this.markers || {
				barUp: new BarMarker(),
				barDown: new BarMarker()
			}
		);
	}

	/**
	 *
	 * @param {object} data - Indicator data
	 * @param {string} type - Indicator type
	 * @returns {array[]}
	 */
	_getDataMap(data, type) {
		switch (type) {
			case 'pos':
				return data.map((d, idx) => {
					const val = d.awesomeoscillator;
					const prev = data[idx - 1] || data[idx];
					const valPrev = prev.awesomeoscillator;

					return [d.dateIndex, val >= valPrev ? val : 0];
				});

			case 'neg':
				return data.map((d, idx) => {
					const val = d.awesomeoscillator;
					const prev = data[idx - 1] || data[idx];
					const valPrev = prev.awesomeoscillator;

					return [d.dateIndex, val < valPrev ? val : 0];
				});

			default:
				return [];
		}
	}

	/** @param {object} data - Symbol data object */
	render(data) {
		const ctx = this.panel.rootContext;
		const x = this.xScale;
		const y = this.yScale;

		this.markers.barUp.render(
			this.panel,
			ctx,
			this._getDataMap(data.awesomeoscillator, 'pos'),
			x,
			y,
			this.params.style.positive.color
		);

		this.markers.barDown.render(
			this.panel,
			ctx,
			this._getDataMap(data.awesomeoscillator, 'neg'),
			x,
			y,
			this.params.style.negative.color
		);
	}
}
